import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { useIsOverflowing } from "shared/hooks";
import { formatStringArray } from "shared/locale/format";
import { splitVisibleItems } from "shared/utils";

import Chip from "features/components/Chip";
import Tooltip from "features/components/Tooltip";

import MoreItemsButton from "./MoreItemsButton";

interface Props {
  items: string[];
  maxItems: number | null;
  separator: string;
  singleLine: boolean;
}

const ChipsCell = ({ items, maxItems, separator, singleLine }: Props) => {
  const { t } = useTranslation();
  const { ref, isOverflowing } = useIsOverflowing();
  const { visible, hidden } = splitVisibleItems(items, maxItems);
  const moreLabel = (count: number) => t("common:chip_list.more", { count });

  const chips = (
    <span
      ref={ref}
      className={classNames("inline-flex gap-1 items-center", {
        "flex-nowrap overflow-hidden min-w-0": singleLine,
        "flex-wrap": !singleLine,
      })}
    >
      {visible.map((item, index) => (
        <Chip key={`${item}-${index}`} label={item} size="small" />
      ))}
    </span>
  );

  // The "+ N more" button sits outside the clipped span, so it stays visible when the chips do not fit.
  return (
    <span className="inline-flex gap-1 items-center max-w-full">
      {isOverflowing ? (
        <Tooltip
          title={formatStringArray(
            items,
            { separator, maxItems: null },
            moreLabel
          )}
        >
          {chips}
        </Tooltip>
      ) : (
        chips
      )}
      {hidden.length > 0 && (
        <MoreItemsButton
          items={items}
          hiddenCount={hidden.length}
          separator={separator}
        />
      )}
    </span>
  );
};

export default ChipsCell;
